import { type RefObject, useState } from 'react';
import { MdExpandMore, MdOutlineEdit, MdSend } from 'react-icons/md';

import { Button } from '@/components/Button';
import { VoiceInputButton } from '@/components/VoiceInputButton';
import { appendTranscript } from '@/utils/transcribeAudio.ts';

import type { AiChatMessage, TaskSuggestion } from '../eventWizard.types';
import s from '../EventWizardModal.module.scss';

interface TasksAiPanelProps {
  open: boolean;
  onToggle: () => void;
  suggestions: TaskSuggestion[];
  chat: AiChatMessage[];
  chatRef: RefObject<HTMLDivElement | null>;
  thinking: boolean;
  promptDraft: string;
  onPromptDraftChange: (value: string) => void;
  onSubmitPrompt: () => void;
  editId: number | null;
  editText: string;
  onEditTextChange: (value: string) => void;
  onStartEdit: (id: number, text: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onAccept: (id: number) => void;
  onReject: (id: number) => void;
  onAcceptAll: () => void;
}

export function TasksAiPanel({
  open,
  onToggle,
  suggestions,
  chat,
  chatRef,
  thinking,
  promptDraft,
  onPromptDraftChange,
  onSubmitPrompt,
  editId,
  editText,
  onEditTextChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onAccept,
  onReject,
  onAcceptAll,
}: TasksAiPanelProps) {
  const [transcribing, setTranscribing] = useState(false);

  return (
    <div className={s.aiPanel}>
      <button type="button" className={s.aiPanelHeader} onClick={onToggle}>
        <span className={s.aiPanelHeaderIcon}>✨</span>
        <span className={s.aiPanelHeaderTitle}>AI Task Assistant</span>
        {open ? (
          <MdExpandMore className={s.aiPanelChevronOpen} />
        ) : (
          <span className={s.aiPanelViewBtn}>
            View {suggestions.length} suggestion
            {suggestions.length !== 1 ? 's' : ''}
          </span>
        )}
      </button>

      {open && (
        <>
          {suggestions.length > 0 && (
            <div className={s.aiSuggestionsList}>
              {suggestions.map((sg) => {
                const editing = editId === sg.id;
                return (
                  <div key={sg.id} className={s.aiSuggestionRow}>
                    <span className={s.aiSuggestionIcon}>☐</span>
                    {editing ? (
                      <input
                        className={s.aiSuggestionEditInput}
                        value={editText}
                        autoFocus
                        onChange={(e) => onEditTextChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            onSaveEdit();
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            onCancelEdit();
                          }
                        }}
                      />
                    ) : (
                      <span className={s.aiSuggestionActivity}>{sg.title}</span>
                    )}
                    <div className={s.aiSuggestionActions}>
                      <button
                        type="button"
                        className={s.aiSuggestionActionBtn}
                        onClick={editing ? onSaveEdit : () => onAccept(sg.id)}
                        aria-label={editing ? 'Save Changes' : 'Accept suggestion'}
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        className={s.aiSuggestionActionBtn}
                        onClick={editing ? onCancelEdit : () => onReject(sg.id)}
                        aria-label={editing ? 'Discard changes' : 'Reject suggestion'}
                      >
                        ✕
                      </button>
                      {!editing && (
                        <button
                          type="button"
                          className={s.aiSuggestionActionBtn}
                          onClick={() => onStartEdit(sg.id, sg.title)}
                          aria-label="Edit suggestion"
                        >
                          <MdOutlineEdit />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {chat.length > 0 && (
            <div className={s.aiChatLog} ref={chatRef}>
              {chat.map((m) => (
                <div
                  key={m.id}
                  className={`${s.aiChatMsg} ${m.role === 'user' ? s.aiChatMsgUser : s.aiChatMsgAi}`}
                >
                  {m.text}
                </div>
              ))}
              {thinking && <div className={`${s.aiChatMsg} ${s.aiChatMsgAi}`}>Thinking…</div>}
            </div>
          )}

          <div className={s.aiPanelPromptRow}>
            <input
              type="text"
              className={s.aiPanelPrompt}
              value={promptDraft}
              onChange={(e) => onPromptDraftChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSubmitPrompt();
                }
              }}
              placeholder="Add a safety briefing"
              disabled={thinking}
            />
            <VoiceInputButton
              source="event_planning_tasks"
              disabled={thinking}
              onTranscribingChange={setTranscribing}
              onTranscript={(text) => onPromptDraftChange(appendTranscript(promptDraft, text))}
            />
            <button
              type="button"
              className={s.aiPanelSend}
              onClick={onSubmitPrompt}
              aria-label="Send prompt"
              disabled={!promptDraft.trim() || thinking || transcribing}
            >
              <MdSend />
            </button>
          </div>

          <div className={s.aiPanelFooter}>
            <div className={s.aiPanelFooterActions}>
              <Button onClick={onAcceptAll} disabled={suggestions.length === 0}>
                Add All
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
